import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import moment from 'moment';
import { DrupalRESTService } from './drupal-rest.service';
import { AuthService } from './auth.service';
import { getTaxTypeShortLabel } from '../constants/tax.constants';

/**
 * How the selected studio's taxes behave, derived from its configuration:tax rows
 * (loadStudioConfig?config_field_name=field_tax_ref).
 *
 * Enrollments store the PRE-TAX price (field_enrollment_total_price), the tax
 * amount (field_taxes) and the pre-tax sum of payments (field_total_payments_made);
 * payments store gross tuition + tax collected separately. For a studio whose
 * taxes are EXCLUSIVE (Canadian GST/HST/PST, added on top of the quoted price)
 * none of those stored figures is what the customer actually owes or paid, so
 * list/dashboard surfaces use this mode to present tax-inclusive totals.
 * Inclusive-tax studios (Australian GST inside the price) and untaxed studios
 * need no adjustment.
 */
export interface StudioTaxMode {
  /** Config has loaded for the current studio (false while the request is in flight). */
  loaded: boolean;
  /** At least one active, currently-effective, percentage tax that is added ON TOP of prices. */
  hasExclusiveTax: boolean;
  /** At least one active, currently-effective, percentage tax already INSIDE prices. */
  hasInclusiveTax: boolean;
  /** Sum of the exclusive percentage rates (e.g. 12 for GST 5 + PST 7). */
  exclusiveRate: number;
  /** Short label for the tax column/line, e.g. "GST", "GST + PST", "HST"; "Tax" when unknown. */
  label: string;
}

const NO_TAX: StudioTaxMode = { loaded: false, hasExclusiveTax: false, hasInclusiveTax: false, exclusiveRate: 0, label: 'Tax' };

@Injectable({
  providedIn: 'root'
})
export class StudioTaxService {
  private mode$ = new BehaviorSubject<StudioTaxMode>(NO_TAX);
  private loadedForStudioId: string | null = null;
  private isLoading = false;

  constructor(
    private drupalRESTService: DrupalRESTService,
    private authService: AuthService,
  ) {
    this.authService.selectedStudioChanged.subscribe(studio => {
      const id = studio?.id?.toString() || '';
      if (id && id !== this.loadedForStudioId) {
        this.load();
      }
    });
  }

  /**
   * Tax mode for the selected studio. Emits once config has loaded (and again on
   * studio switch); callers never see the pre-load placeholder.
   */
  getTaxMode(): Observable<StudioTaxMode> {
    if (this.loadedForStudioId !== this.currentStudioId()) {
      this.load();
    }
    return this.mode$.asObservable().pipe(
      filter(m => m.loaded),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );
  }

  /** Last resolved mode, synchronously (placeholder with loaded=false before the first load). */
  get current(): StudioTaxMode {
    return this.mode$.value;
  }

  private currentStudioId(): string {
    return (this.authService.selectedStudio?.id || this.authService.studios?.[0]?.id || '').toString();
  }

  private load(): void {
    const studioId = this.currentStudioId();
    if (!studioId || this.isLoading) return;
    this.isLoading = true;
    this.mode$.next(NO_TAX);

    this.drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_tax_ref').subscribe(
      (response: any) => {
        const rows = Array.isArray(response) ? response : (response ? [response] : []);
        this.mode$.next(StudioTaxService.resolve(rows));
        this.loadedForStudioId = studioId;
        this.isLoading = false;
      },
      () => {
        this.mode$.next({ ...NO_TAX, loaded: true });
        this.loadedForStudioId = studioId;
        this.isLoading = false;
      }
    );
  }

  /** Pure reduction of configuration:tax rows to a StudioTaxMode (same rules the enrollment form applies). */
  static resolve(rows: any[]): StudioTaxMode {
    const now = moment();
    let exclusiveRate = 0;
    let inclusiveRate = 0;
    const labels: string[] = [];

    for (const tax of rows) {
      const val = (f: string) => Array.isArray(tax?.[f]) ? tax[f][0]?.value : tax?.[f];
      const status = val('field_tax_status');
      if (status !== '1' && status !== 'true' && status !== true) continue;
      const start = val('field_effective_tax_start_date');
      const end = val('field_effective_tax_end_date');
      if (start && now.isBefore(moment(start))) continue;
      if (end && now.isAfter(moment(end))) continue;
      if (val('field_calculation_method') !== 'PERCENTAGE') continue;
      const rate = parseFloat(val('field_tax_value'));
      if (isNaN(rate)) continue;

      const inclusive = val('field_is_tax_inclusive');
      if (inclusive === '1' || inclusive === 'true' || inclusive === true) {
        inclusiveRate += rate;
      } else {
        exclusiveRate += rate;
      }
      const label = getTaxTypeShortLabel(val('field_tax_type'), 'Tax');
      if (!labels.includes(label)) labels.push(label);
    }

    return {
      loaded: true,
      hasExclusiveTax: exclusiveRate > 0,
      hasInclusiveTax: inclusiveRate > 0,
      exclusiveRate,
      label: labels.length ? labels.join(' + ') : 'Tax',
    };
  }
}

/**
 * Tax-inclusive view of one enrollment row from the enrollment_list view
 * (string-valued fields). Returns null when there is nothing to add: no
 * exclusive tax on the studio, or no tax on this enrollment, so templates can
 * fall back to the stored figures untouched.
 *
 * Paid-with-tax uses the ACTUAL collected tax when the caller supplies it
 * (from /api/v1/enrollment-list/paid-tax). The payments' own rounding is the
 * truth, and prorating can land a cent off it (e.g. $769.15 at 5% prorates to
 * $38.46 while the payments really collected $38.45). Any paid amount beyond
 * the actual sums (multi-enrollment payments, which store no per-enrollment
 * tax split) still prorates from the enrollment's tax ratio
 * (field_taxes / field_enrollment_total_price), which is exact whenever the
 * rate did not change mid-enrollment.
 */
export function enrollmentWithTax(row: any, mode: StudioTaxMode | null, actualPaid?: { tax: number; gross: number } | null): { total: number; paid: number; due: number; totalTax: number; paidTax: number; dueTax: number } | null {
  if (!mode?.hasExclusiveTax) return null;
  const price = parseFloat(row?.field_enrollment_total_price) || 0;
  const tax = parseFloat(row?.field_taxes) || 0;
  if (!(tax > 0) || !(price > 0)) return null;

  const paid = parseFloat(row?.field_total_payments_made) || 0;
  const due = row?.field_balance_due !== undefined && row?.field_balance_due !== ''
    ? (parseFloat(row.field_balance_due) || 0)
    : price - paid;
  const ratio = tax / price;
  // Rows served by /api/v1/enrollment-list embed the actuals directly.
  const actual = actualPaid ?? (row?.paid_gross_actual
    ? { tax: parseFloat(row.paid_tax_actual) || 0, gross: parseFloat(row.paid_gross_actual) || 0 }
    : null);
  let paidTax: number;
  if (actual && actual.gross > 0) {
    const unattributed = Math.max(0, paid - actual.gross);
    paidTax = round2(actual.tax + unattributed * ratio);
  } else {
    paidTax = round2(paid * ratio);
  }
  // Once the pre-tax balance is paid off the enrollment is settled. The
  // estimate was rounded once on the full price ($8,662.50 at 13% is
  // $1,126.125 -> $1,126.13) while each payment rounds its own share
  // ($575.22 + $550.90 = $1,126.12), so the two can differ by a cent that no
  // further payment can collect; it must not show as $0.01 due.
  const dueTax = due > 0 ? Math.max(0, round2(tax - paidTax)) : 0;

  return {
    totalTax: tax,
    paidTax,
    dueTax,
    total: round2(price + tax),
    paid: round2(paid + paidTax),
    due: round2(due + dueTax),
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** One tax added on top of prices: its percentage and how its dollars round. */
export interface ExclusiveTaxRow {
  rate: number;
  roundingRule: string;
}

/**
 * Round a dollar amount to cents by a configured rounding rule. The amount is
 * snapped in cent space first so an exact half cent ($100 × 9.975% = $9.975)
 * rounds by the rule, not by its binary representation ($9.97499999…).
 */
export function roundTaxDollars(value: number, rule: string): number {
  const cents = Math.round(value * 100 * 10000) / 10000;
  switch (rule) {
    case 'ROUND_UP':
      return Math.ceil(cents) / 100;
    case 'ROUND_DOWN':
      return Math.floor(cents) / 100;
    case 'ROUND_NEAREST':
    case 'NO_ROUNDING':
    default:
      return Math.round(cents) / 100;
  }
}

/** Tax added on top of a pre-tax amount: each tax rounded by its own rule, then summed. */
export function forwardTaxDollars(gross: number, rows: ExclusiveTaxRow[]): number {
  return roundTaxDollars(rows.reduce((sum, r) => sum + roundTaxDollars(gross * (r.rate / 100), r.roundingRule), 0), 'ROUND_NEAREST');
}

/**
 * THE split of a typed tax-inclusive total into pre-tax + tax, shared by every
 * surface that backs a customer figure out (scheduled-row Total column, the
 * down payment, and the payment screen's Total box). gross + tax always
 * reproduces the typed figure to the cent.
 *
 * Preference order matters for penny-consistency across surfaces:
 *  1. A pre-tax amount whose FORWARD tax (the way generated rows are built)
 *     lands exactly on the total — searched ±2¢ around total ÷ (1 + rate).
 *     This makes the split the exact inverse of row generation, so paying a
 *     generated row's Total records precisely the row's pre-tax amount.
 *  2. Otherwise the total sits in a rounding gap no pre-tax cent maps to:
 *     extract each tax as its share of the total (rate_i ÷ (1 + rate_sum))
 *     and let gross take the remainder.
 * Two surfaces using different methods (division-then-forward vs extraction)
 * disagreed on the gross ~25% of the time at dual-tax studios (GST + PST/QST),
 * leaving 1¢ owing on a scheduled row after its exact Total was paid.
 */
export function splitInclusiveTotal(total: number, rows: ExclusiveTaxRow[]): { gross: number; tax: number } {
  const t = round2(total);
  const rateSum = rows.reduce((sum, r) => sum + r.rate, 0);
  if (!rows.length || rateSum <= 0) {
    return { gross: t, tax: 0 };
  }

  const base = round2(t / (1 + rateSum / 100));
  for (const delta of [0, -0.01, 0.01, -0.02, 0.02]) {
    const gross = round2(base + delta);
    if (gross < 0) continue;
    const tax = forwardTaxDollars(gross, rows);
    if (round2(gross + tax) === t) {
      return { gross, tax };
    }
  }

  let tax = 0;
  for (const r of rows) {
    tax += roundTaxDollars(t * (r.rate / 100) / (1 + rateSum / 100), r.roundingRule);
  }
  tax = roundTaxDollars(tax, 'ROUND_NEAREST');
  return { gross: round2(t - tax), tax };
}

/** One scheduled row of an enrollment: its stored pre-tax amount and tax, in dollars. */
export interface ScheduledRowAmounts {
  gross: number;
  tax: number;
}

/**
 * Pre-tax amount to record for a typed tax-inclusive Total that is exactly
 * what the enrollment still owes through one of its scheduled rows, or null
 * when the Total is not a row boundary or the split already matches.
 *
 * The schedule and splitInclusiveTotal() back the same customer figure out
 * independently and can land a cent apart: a $1,800 row stored as $1,714.28
 * + $85.72 vs $1,800 divided to $1,714.29 + $85.71. The pre-tax waterfall
 * then overfills the row and the cent lands as "paid" on the next one, so
 * the next installment shows $1,799.99 owing. A Total that pays a row to the
 * cent is that row's money, so record the row's own pre-tax amount. Mirrors
 * amt_payment_scheduled_row_snap_cents() on the server, which applies the
 * same rule to whatever is posted.
 */
export function grossOnScheduledRowBoundary(total: number, gross: number, rows: ScheduledRowAmounts[], paidGross: number, paidTax: number): number | null {
  const cents = (v: number) => Math.round(v * 100);
  const diff = scheduledRowSnapCents(
    rows.map(r => ({ gross: cents(r.gross), tax: cents(r.tax) })),
    cents(paidGross), cents(paidTax), cents(gross), cents(total) - cents(gross));
  return diff === 0 ? null : round2(gross + diff / 100);
}

/**
 * Cents to add to a payment's pre-tax amount (and take from its tax) so it
 * lands the schedule on a row boundary; 0 when nothing should move. Only a
 * rounding difference moves: cents carried in by earlier receipts accumulate
 * down the schedule, so up to two cents per row from the top of the schedule
 * to that boundary, ten at most.
 */
export function scheduledRowSnapCents(rows: { gross: number; tax: number }[], paidGrossCents: number, paidTaxCents: number, grossCents: number, taxCents: number): number {
  if (grossCents <= 0 || taxCents <= 0 || !rows.length) {
    return 0;
  }
  const paymentTotal = grossCents + taxCents;
  const paidTotal = paidGrossCents + paidTaxCents;
  let runningGross = 0;
  let runningTax = 0;
  let rowsSeen = 0;
  for (const row of rows) {
    const rowGross = Math.round(row.gross) || 0;
    const rowTax = Math.round(row.tax) || 0;
    if (rowGross <= 0 || rowTax < 0) continue;
    rowsSeen++;
    runningGross += rowGross;
    runningTax += rowTax;
    const owedThroughRow = runningGross + runningTax - paidTotal;
    if (owedThroughRow <= 0) continue;
    if (owedThroughRow > paymentTotal) return 0;
    if (owedThroughRow < paymentTotal) continue;
    const targetGross = runningGross - paidGrossCents;
    const diff = targetGross - grossCents;
    if (diff === 0 || targetGross <= 0 || taxCents - diff < 0 || Math.abs(diff) > Math.min(10, 2 * rowsSeen)) {
      return 0;
    }
    return diff;
  }
  return 0;
}
